import { v4 as UUIDv4 } from 'uuid';
import { temporal } from 'zundo';
import omit from 'lodash/omit';
import clone from 'lodash/clone';
import throttle from 'lodash/throttle';
import { createWithEqualityFn, useStoreWithEqualityFn } from 'zustand/traditional';
import { initialGeneratorParams, MAX_GENERATOR_SIZE } from '@shakkerai/pro-constants';
import {
  defaultOutpaintModelKey,
  fallbackModelBaseParams,
  MODEL_FLUX,
  MODEL_SDXL,
  outpaintPresetModelsMap,
  SD3_MODELS_FAMILY,
} from '@shakkerai/pro-models';
import { baseTypeToNumber, deleteElements, updateElement } from '@shakkerai/pro-utils';
import { groupElements, regroupElements, ungroupElements, calcMultiImageRect } from '@shakkerai/pro-utils';
import { fixControlNets, fixGenerateParamsForBaseType } from '../utils/data';
import { fixGenerateParamsForControlNets } from '../utils/data';
import { throttledSaveCloudData } from '../utils';
import { getTempState, useTempState } from './temp';

export const APP_VERSION = 2.1;

const checkIfWithFaceCN = () => {};

const cloudPersist = (config) => (set, get, api) => {
  return config(
    (nextState, replace) => {
      const skipSaving = nextState.__skipSaving;

      nextState.__skipSaving = false;
      nextState.__version = get().__version + 1;

      set(nextState, replace);

      if (!skipSaving) {
        throttledSaveCloudData();
      }
    },
    get,
    api,
  );
};

const executeOrReturn = (newValue, prevValue) => {
  return typeof newValue === 'function' ? newValue(prevValue) : newValue;
};

export const getGenerateSizeFromElement = (element) => {
  const { width, height, scaleX, scaleY } = element;

  let fixedWidth = Math.round(width * scaleX);
  let fixedHeight = Math.round(height * scaleY);
  let scale = 1;

  if (fixedWidth > MAX_GENERATOR_SIZE || fixedHeight > MAX_GENERATOR_SIZE) {
    scale = Math.min(MAX_GENERATOR_SIZE / fixedWidth, MAX_GENERATOR_SIZE / fixedHeight);
    fixedWidth = Math.round(fixedWidth * scale);
    fixedHeight = Math.round(fixedHeight * scale);
  }

  return { width: fixedWidth, height: fixedHeight, scale };
};

export const fixSourceImageSIze = (params) => {
  const { width, height } = params;

  let fixedWidth = Math.round(width);
  let fixedHeight = Math.round(height);
  let scale = 1;

  if (fixedWidth > MAX_GENERATOR_SIZE || fixedHeight > MAX_GENERATOR_SIZE) {
    scale = Math.min(MAX_GENERATOR_SIZE / fixedWidth, MAX_GENERATOR_SIZE / fixedHeight);
    fixedWidth = Math.round(fixedWidth * scale);
    fixedHeight = Math.round(fixedHeight * scale);
  }

  return { width: fixedWidth, height: fixedHeight, scale };
};

export const createInpaintGenerator = (element, width, height, genInfo) => {
  const originalParams = Object.assign({}, initialGenerateParams, genInfo);
  const params = JSON.parse(JSON.stringify(originalParams));

  params.changed = false;
  params.hires = false;
  params.controlnets = [];
  params.strength = 40;
  params.width = width;
  params.height = height;
  params.count = 1;
  params.seedType = '0';
  params.modelPreset = element.innerTaskId ? params.modelPreset || 'original' : 'custom';
  params.originalBaseType = originalParams.baseType;
  params.originalCfgScale = originalParams.modelCfgScale || originalParams.cfgScale;
  params.cfgScale = params.originalCfgScale;
  params.prompt = '';

  if (params.cfgScale === 2.5 && checkIfWithFaceCN(originalParams.controlnets)) {
    params.cfgScale =
      originalParams.modelCfgScale ||
      fallbackModelBaseParams[params.baseType]?.cfgScale ||
      initialGenerateParams.cfgScale;
  }

  if (params.modelPreset !== 'original' && params.baseType === MODEL_FLUX) {
    params.cfgScale = 7;
  }

  if (params.modelPreset !== 'common' && params.modelPreset !== 'original') {
    params.baseType = MODEL_SDXL;
    params.checkpoint = null;
    params.loraModels = [];
  }

  if (SD3_MODELS_FAMILY[params.baseType] && params.baseType !== MODEL_FLUX) {
    params.originalBaseType = params.baseType;
    params.baseType = MODEL_SDXL;
    params.checkpoint = null;
    params.loraModels = [];
    params.modelPreset = 'common';
  }

  if (params.baseType === MODEL_FLUX) {
    params.strength = 40;
  }

  params.originalCfgScale = params.cfgScale;
  params.originalParams = JSON.parse(JSON.stringify(params));

  return {
    ...initialGenerator,
    id: element.id.replace('elementImage', 'generatorInpainting'),
    type: 'inpaint',
    visible: false,
    params: params,
  };
};

export const createChangefaceGenerator = (element, width, height, genInfo) => {
  const originalParams = Object.assign({}, initialGenerateParams, genInfo);
  const params = JSON.parse(JSON.stringify(originalParams));

  params.changed = false;
  params.hires = false;
  params.controlnets = [];
  params.strength = 100;
  params.width = width;
  params.height = height;
  params.count = 1;
  params.seedType = '0';
  params.modelPreset = 'common';
  params.changeFaceUrl = '';

  return {
    ...initialGenerator,
    id: element.id.replace('elementImage', 'generatorChangeface'),
    type: 'changeFace',
    visible: false,
    params: params,
  };
};

export const createUpscaleGenerator = (element, genInfo) => {
  const originalParams = Object.assign({}, initialGenerateParams, genInfo);
  const params = JSON.parse(JSON.stringify(originalParams));

  params.controlnets = [];
  params.width = Math.round(element.width * (element.upscale?.scale || 1));
  params.height = Math.round(element.height * (element.upscale?.scale || 1));
  params.samplingSteps = params.samplingSteps || initialGenerateParams.samplingSteps;
  params.count = 1;
  params.detailFactor = 0.3;
  params.upscaleType = '1';
  params.scaleFactor = 2;
  params.strength = 50;
  params.upscale_strength = 0.75;
  params.upscale_lora_weight = 0.72;
  params.upscale_cfgScale = 0;
  params.upscale_controlWeight = 0.84;

  if (params.cfgScale === 2.5 && checkIfWithFaceCN(originalParams.controlnets)) {
    params.cfgScale =
      originalParams.modelCfgScale ||
      fallbackModelBaseParams[params.baseType]?.cfgScale ||
      initialGenerateParams.cfgScale;
  }

  if (params.acturalSeed) {
    params.seedNumber = params.acturalSeed;
  }

  return {
    ...initialGenerator,
    id: element.id.replace('elementImage', 'generatorUpscale'),
    type: 'upscale',
    x: element.x,
    y: element.y,
    params: params,
  };
};

export const createOutpaintGenerator = (element, genInfo) => {
  const originalParams = Object.assign({}, initialGenerateParams, genInfo);
  const params = JSON.parse(JSON.stringify(originalParams));
  const maxSide = Math.max(Math.round(element.width), Math.round(element.height));
  params.controlnets = [];
  params.x = 0;
  params.y = 0;
  params.count = 2;
  params.checkpointId = params.checkpointId || outpaintPresetModelsMap[defaultOutpaintModelKey].modelVersionId;
  params.width = maxSide;
  params.height = maxSide;
  params.samplingSteps = params.samplingSteps || initialGenerateParams.samplingSteps;
  params.strength = 50;
  params.cfgScale = 7;
  params.negativePrompt = '';
  params.presetNegativePrompts = [];
  params.prompt = genInfo?.prompt || '';

  if (params.cfgScale === 2.5 && checkIfWithFaceCN(originalParams.controlnets)) {
    params.cfgScale =
      originalParams.modelCfgScale ||
      fallbackModelBaseParams[params.baseType]?.cfgScale ||
      initialGenerateParams.cfgScale;
  }

  if (params.acturalSeed) {
    params.seedNumber = params.acturalSeed;
  }

  return {
    ...initialGenerator,
    id: element.id.replace('elementImage', 'generatorOutpaint'),
    type: 'outpaint',
    x: element.x,
    y: element.y,
    params: params,
  };
};

export const createCollageGenerator = (elements, group) => {
  let type = null;
  let size = null;
  let generatorId = null;

  if (elements) {
    type = 'collage-elements';
    size = calcMultiImageRect(elements);
    generatorId = 'generatorCollageElements' + UUIDv4();
  } else if (group) {
    type = 'collage-group';
    size = group;
    generatorId = 'generatorCollageGroup' + UUIDv4();
  }

  return {
    ...initialGenerator,
    id: generatorId,
    type: type,
    x: 0,
    y: 0,
    params: Object.assign({}, clone(initialGenerateParams), {
      width: Math.round(size.width),
      height: Math.round(size.height),
      strength: 50,
    }),
  };
};

export const initialTaskData = {
  status: 'idle',
  progress: -1,
  images: [],
};

export const initialGenerateParams = initialGeneratorParams.primary;

export const initialGenerator = {
  id: 'generatorPrimary' + UUIDv4(),
  type: 'primary',
  x: 0,
  y: 0,
  visible: false,
  params: initialGenerateParams,
};

export const initialCoreData = {
  __version: 1,
  appVersion: APP_VERSION,
  initialized: false,
  isGuestData: true,
  selectionElements: [],
  generators: [initialGenerator],
  elements: [],
  backgroundColor: '#f2f2f2ff',
  openedSubWindow: null,
  taskMap: { [initialGenerator.id]: initialTaskData },
  taskQueue: [],
};

/**
 * 画布核心数据，这一部分是支持撤销重做，然后需要存储到云端的
 */
export const _useCanvasState = createWithEqualityFn(
  cloudPersist(
    temporal(
      (set, get) => ({
        ...initialCoreData,

        updateSelection: (selectionElements, options = { skipTracking: false }) => {
          if (selectionElements && selectionElements.length === 1) {
            const __selectionElements = get().selectionElements;

            if (
              __selectionElements &&
              __selectionElements.length === 1 &&
              __selectionElements[0] === selectionElements[0]
            ) {
              return;
            }
          }

          set({ selectionElements, __skipTracking: options.skipTracking });
        },

        updateBackgroundColor: (color, options = { skipTracking: false }) => {
          set({ backgroundColor: color, __skipTracking: options.skipTracking });
        },

        toggleSelection: (elementId, options = { skipTracking: false }) => {
          const selectionElements = get().selectionElements;
          if (!selectionElements.includes(elementId)) {
            set({ selectionElements: selectionElements.concat(elementId), __skipTracking: options.skipTracking });
          } else {
            set({
              selectionElements: selectionElements.filter((id) => id !== elementId),
              __skipTracking: options.skipTracking,
            });
          }
        },

        appendSelection: (elementId, options = { skipTracking: false }) => {
          const selectionElements = get().selectionElements;
          if (!selectionElements.includes(elementId)) {
            set({ selectionElements: selectionElements.concat(elementId), __skipTracking: options.skipTracking });
          } else {
            set({
              selectionElements: selectionElements.filter((id) => id !== elementId),
              __skipTracking: options.skipTracking,
            });
          }
        },

        updateCanvasData: (canvasData, options = { skipTracking: false }) => {
          set(
            options.override
              ? canvasData
              : Object.assign({}, get(), { __skipTracking: options.skipTracking }, canvasData),
          );
        },

        updateGenerator: (generatorId, updatedGenerator, options = { override: false, skipTracking: false }) => {
          const currentData = get();
          const { selectionElements, taskMap, generators } = currentData;

          if (!generatorId) {
            if (selectionElements.length === 1) {
              if (selectionElements[0].startsWith('generator')) {
                generatorId = selectionElements[0];
              } else if (selectionElements[0].startsWith('elementImage')) {
                generatorId = selectionElements[0].replace('elementImage', 'generatorInpainting');
                if (!taskMap[generatorId]) generatorId = null;
              }
            }
          }

          generatorId = generatorId || generators[0].id;

          const nextGenerators = generators.map((generator) => {
            if (generator.id === generatorId) {
              updatedGenerator = executeOrReturn(updatedGenerator, generator);
              return options.override ? updatedGenerator : Object.assign({}, generator, updatedGenerator);
            }

            return generator;
          });

          set({ generators: nextGenerators, __skipTracking: options.skipTracking });
        },

        appendTaskQueue: (data) => {
          set({ taskQueue: get().taskQueue.concat(data) });
        },

        updateTaskQueue: (id, data) => {
          const nextTaskQueue = get().taskQueue.map((item) => {
            if (item.id !== id) return item;
            return Object.assign({}, item, executeOrReturn(data, item));
          });

          set({ taskQueue: nextTaskQueue });
        },

        removeTaskQueue: (id) => {
          set({ taskQueue: get().taskQueue.filter((item) => item.id !== id) });
        },

        // 开启某个图片的局部重绘模式
        startInpainting: (id, options = { skipTracking: false }) => {
          const { elements } = get();

          const nextData = {
            elements: elements.map((element) => {
              if (element.id === id) {
                return Object.assign({}, element, {
                  inpaint: {
                    ...element.inpaint,
                    enabled: true,
                  },
                });
              }

              return element;
            }),
            __skipTracking: options.skipTracking,
          };

          nextData.activeCrop = null;
          nextData.activeCrop = null;
          nextData.activeRemove = null;
          nextData.activeSmartRemove = null;

          set(nextData);
        },

        // 更新重绘状态
        updateInpainting: (id, data, options = { skipTracking: false, type: 'inpaint' }) => {
          let temp = null;
          const type = options.type;
          const currentImageTool = getTempState('topImagesTool');
          const nextData = {
            elements: get().elements.map((element) => {
              if (element.id === id) {
                // 处理各个类型的selection
                temp = Object.assign({}, element[type], executeOrReturn(data, element[type]), {
                  mode: currentImageTool,
                });
                return Object.assign({}, element, { [type]: temp });
              }
              return element;
            }),
            __skipTracking: options.skipTracking,
          };

          set(nextData);
        },

        cancelInpainting: (id, options = { skipTracking: false }) => {
          if (!id) return;

          const { elements, generators, taskMap } = get();

          const inpaintGeneratorId = id.replace('elementImage', 'generatorInpainting');
          const clearInpaint = taskMap[inpaintGeneratorId]?.status === 'success';

          const nextData = {
            elements: elements.map((element) => {
              if (element.id !== id || !element.inpaint) return element;

              const inpaint = element.inpaint || {};

              return Object.assign({}, element, {
                inpaint: Object.assign({}, inpaint, {
                  enabled: false,
                  currentSelectionType: clearInpaint ? 'segment' : inpaint.currentSelectionType || 'segment',
                  selections: clearInpaint ? [] : inpaint.selections,
                  generatorId: clearInpaint ? null : inpaint.generatorId,
                  status: clearInpaint ? 'idle' : inpaint.status,
                }),
              });
            }),
            __skipTracking: options.skipTracking,
          };

          if (clearInpaint) {
            // 已经生图成功的话，取消的时候把生图结果丢弃掉
            nextData.generators = generators.filter((item) => item.id !== inpaintGeneratorId);
            // nextData.taskMap = omit(taskMap, id);
          }

          set(nextData);
        },

        updateElements: (elements, options = { skipTracking: false }) => {
          if (typeof elements === 'function') {
            set({ elements: elements(get().elements), __skipTracking: options.skipTracking });
          } else {
            set({ elements, __skipTracking: options.skipTracking });
          }
        },

        addElement: (element, options = { autoSelect: false, skipTracking: false }) => {
          if (options.autoSelect) {
            set({
              elements: get().elements.concat(element),
              selectionElements: [element.id],
              __skipTracking: options.skipTracking,
            });
          } else {
            set({ elements: get().elements.concat(element), __skipTracking: options.skipTracking });
          }
        },

        updateElement: (id, updatedElement, options = { skipTracking: false }) => {
          const elements = updateElement(id, updatedElement, get().elements, options);

          set({ elements, __skipTracking: options.skipTracking });
        },

        bringElementToFront(id, options = { skipTracking: false }) {
          const elements = get().elements;
          const index = elements.findIndex((element) => element.id === id);
          if (index === -1 || index === elements.length - 1) return;

          const nextElements = elements.slice();
          const [element] = nextElements.splice(index, 1);
          nextElements.push(element);

          set({ elements: nextElements, __skipTracking: options.skipTracking });
        },

        sendElementToBack(id, options = { skipTracking: false }) {
          const elements = get().elements;
          const index = elements.findIndex((element) => element.id === id);
          if (index === -1 || index === 0) return;

          const nextElements = elements.slice();
          const [element] = nextElements.splice(index, 1);
          nextElements.unshift(element);

          set({ elements: nextElements, __skipTracking: options.skipTracking });
        },

        moveUpElement: (id, options = { skipTracking: false }) => {
          const elements = get().elements;
          const index = elements.findIndex((element) => element.id === id);
          if (index === -1 || index === elements.length - 1) return;

          const nextElements = elements.slice();
          const [element] = nextElements.splice(index, 1);
          nextElements.splice(index + 1, 0, element);

          set({ elements: nextElements, __skipTracking: options.skipTracking });
        },

        moveDownElement: (id, options = { skipTracking: false }) => {
          const elements = get().elements;
          const index = elements.findIndex((element) => element.id === id);
          if (index === -1 || index === 0) return;

          const nextElements = elements.slice();
          const [element] = nextElements.splice(index, 1);
          nextElements.splice(index - 1, 0, element);

          set({ elements: nextElements, __skipTracking: options.skipTracking });
        },

        updateElementIndex: (id, index, options = { skipTracking: false }) => {
          const elements = get().elements;
          const currentIndex = elements.findIndex((element) => element.id === id);
          if (currentIndex === -1 || currentIndex === index) return;

          const nextElements = elements.slice();
          const [element] = nextElements.splice(currentIndex, 1);
          nextElements.splice(index, 0, element);

          set({ elements: nextElements, __skipTracking: options.skipTracking });
        },

        renameElement: (id, name, options = { skipTracking: false }) => {
          const elements = get().elements.map((element) => {
            if (element.id === id) {
              return Object.assign({}, element, { name });
            }
            return element;
          });

          set({ elements, __skipTracking: options.skipTracking });
        },

        deleteElements: (ids, options = { skipTracking: false }) => {
          const { elements, generators, taskQueue, taskMap } = get();
          let nextGenerators = generators;

          // 如果存在正在进行中的任务，则不能删除
          const taskIds = Object.keys(taskMap);

          ids = ids.filter((id) => {
            const idSuffix = id.replace('elementImage', '');
            const imageTasks = taskIds.filter((taskId) => taskId.includes(idSuffix)).map((taskId) => taskMap[taskId]);

            const hasRunningTask = imageTasks.some((task) => task.status !== 'idle' && task.status !== 'error');

            if (hasRunningTask) {
              // notifyWarn(translate('有图片上有任务正在进行中，不能删除'));
              return false;
            }

            return true;
          });

          const deletedIdsMap = Object.fromEntries(ids.map((id) => [id, true]));

          const nextElements = deleteElements(ids, elements);
          const deletedElements = elements.filter((element) => deletedIdsMap[element.id]);
          const deletedInpaitingGenerators = deletedElements
            .filter((item) => item.inpaint?.generatorId)
            .map((item) => item.inpaint.generatorId);

          // 如果这个图片上有排队中的生图任务，也要删掉～
          const nextTaskQueue = taskQueue.filter((item) => {
            if (item.type === 'primary') return true;
            return !item.sourceElement || !deletedIdsMap[item.sourceElement?.id];
          });

          if (deletedInpaitingGenerators.length) {
            nextGenerators = nextGenerators.filter((item) => !deletedInpaitingGenerators.includes(item.id));
          }

          const nextData = {
            elements: nextElements,
            taskQueue: nextTaskQueue,
            generators: nextGenerators,
            __skipTracking: options.skipTracking,
          };

          set(nextData);
        },

        deleteElement: (id, options = { skipTracking: false }) => {
          const { elements, generators, taskQueue, taskMap } = get();

          const idSuffix = id.replace('elementImage', '');
          const imagesTasks = Object.keys(taskMap)
            .filter((taskId) => taskId.includes(idSuffix))
            .map((taskId) => taskMap[taskId]);

          // 如果存在正在进行中的任务，则不能删除
          if (imagesTasks.some((task) => task.status !== 'idle' && task.status !== 'error')) {
            return;
          }

          let nextGenerators = generators;
          const nextElements = elements.filter((element) => element.id !== id);
          const deletedElement = elements.find((element) => element.id === id);

          if (deletedElement?.inpaint?.generatorId) {
            nextGenerators = nextGenerators.filter((item) => item.id !== deletedElement.inpaint.generatorId);
          }

          // 如果这个图片上有排队中的生图任务，也要删掉～
          const nextTaskQueue = taskQueue.filter((item) => {
            if (item.type === 'primary') return true;
            return !item.sourceElement || item.sourceElement.id !== id;
          });

          const nextData = {
            elements: nextElements,
            taskQueue: nextTaskQueue,
            generators: nextGenerators,
            __skipTracking: options.skipTracking,
          };

          set(nextData);
        },

        groupElements: (ids, options = { skipTracking: false }) => {
          const [nextElements, group] = groupElements(ids, get().elements.slice());

          set({
            selectionElements: [group.id],
            elements: nextElements,
            __skipTracking: options.skipTracking,
          });
        },

        ungroupElements: (ids, options = { skipTracking: false }) => {
          const elements = ungroupElements(ids, get().elements.slice());
          set({
            elements,
            __skipTracking: options.skipTracking,
          });
        },

        regroupElements: (group, preUpdate, options = { skipTracking: false }) => {
          // const elements = get().elements.map(element => {
          //   if (element.id === group.id) {
          //     return regroup(group)
          //   }
          //   return element
          // })
          set({
            elements: regroupElements(group.id, preUpdate, get().elements.slice()),
            selectionElements: [...get().selectionElements],
            __skipTracking: options.skipTracking,
          });
        },

        addElementsToGroup: (groupId, ids, targetIndex, options = { skipTracking: false }) => {
          const elements = get().elements;
          const group = elements.find((element) => element.id === groupId);
          if (!group || group.type !== 'group') return;

          const nextElements = elements
            .filter((element) => element.id !== groupId)
            .concat({
              ...group,
              elements: group.elements
                .slice(0, targetIndex)
                .concat(elements.filter((element) => ids.includes(element.id)))
                .concat(group.elements.slice(targetIndex)),
            });

          set({ elements: nextElements, __skipTracking: options.skipTracking });
        },

        removeElementsFromGroup: (groupId, ids, options = { skipTracking: false }) => {
          const elements = get().elements;
          const group = elements.find((element) => element.id === groupId);
          if (!group || group.type !== 'group') return;

          const nextElements = elements
            .filter((element) => element.id !== groupId)
            .concat({
              ...group,
              elements: group.elements.filter((element) => !ids.includes(element.id)),
            });

          set({ elements: nextElements, __skipTracking: options.skipTracking });
        },

        moveElementOutFromGroup: (groupId, id, options = { skipTracking: false }) => {
          const elements = get().elements;
          const group = elements.find((element) => element.id === groupId);
          if (!group || group.type !== 'group') return;

          const nextElements = elements
            .filter((element) => element.id !== groupId)
            .concat({
              ...group,
              elements: group.elements.filter((element) => element.id !== id),
            })
            .concat(elements.filter((element) => element.id === id));

          set({ elements: nextElements, __skipTracking: options.skipTracking });
        },

        openOrCloseSubWindowFromButton: (event) => {
          const currentOpenedSubWindow = get().openedSubWindow;
          const subWindow = event.currentTarget.dataset.triggerWindow;
          set({ openedSubWindow: currentOpenedSubWindow === subWindow ? '' : subWindow });
        },

        closeOpenedSubWindow: (options = { skipTracking: false }) => {
          set({ openedSubWindow: null, __skipTracking: options.skipTracking });
        },

        updateOpenedSubWindow: (openedSubWindow, options = { skipTracking: false }) => {
          set({ openedSubWindow, __skipTracking: options.skipTracking });
        },
      }),
      {
        // 最多保存100条历史记录
        limit: 100,
        diff: (_, currentState) => {
          // 未登录的数据不做历史记录
          if (currentState.isGuestData) return null;

          // 如果传递了 __noTracking 标记，那么就不需要记录历史了
          if (currentState.__skipTracking) return null;

          return undefined;
        },
        partialize: (state) => omit(state, ['taskMap']),
        handleSet: (handleSet) =>
          throttle((state) => {
            handleSet(state);
          }, 50),
      },
    ),
  ),
);

export const useCanvasState = (fieldName) => {
  if (typeof fieldName === 'string') {
    return _useCanvasState((state) => state[fieldName]);
  }

  if (typeof fieldName === 'function') {
    return _useCanvasState(fieldName);
  }

  if (fieldName === false) {
    return _useCanvasState.getState();
  }

  return _useCanvasState();
};

export const setCanvasState = (nextState, options) => {
  _useCanvasState.setState(nextState, options);
};

export const getCanvasState = (fieldName) => {
  return fieldName ? _useCanvasState.getState()[fieldName] : _useCanvasState.getState();
};

export const useCanvasStateUndoRedo = (selector, equality) =>
  useStoreWithEqualityFn(_useCanvasState.temporal, selector, equality);

export const getGeneratorParams = (generatorId, fieldName) => {
  const state = _useCanvasState.getState();
  const generator = state.generators.find((item) => item.id === generatorId);
  return generator ? (fieldName ? generator.params[fieldName] : generator.params) : null;
};

export const useGeneratorParams = (generatorId, fieldName) => {
  return _useCanvasState((state) => {
    const { selectionElements, generators, taskMap } = state;

    let gId = generatorId;
    if (!gId) {
      if (selectionElements.length === 1) {
        if (selectionElements[0].startsWith('generator')) {
          gId = selectionElements[0];
        } else if (selectionElements[0].startsWith('elementImage')) {
          gId = selectionElements[0].replace('elementImage', 'generatorPrimary');
          if (!taskMap[generatorId]) gId = null;
        }
      }
    }

    gId = gId || generators[0].id;
    const generator = generators.find((item) => item.id === gId);

    return generator ? (fieldName ? generator.params[fieldName] : generator.params || {}) : {};
  });
};

// 一些比较深层的操作canvas state的方法
export const updateGenerateParams = (generatorId, params, options) => {
  getCanvasState().updateGenerator(
    generatorId,
    (generator) => {
      const prevBaseType = generator.params.baseType;
      let nextBaseType = params.baseType;

      let nextParams = Object.assign({}, generator.params, executeOrReturn(params, generator.params), {
        changed: !options?.skipRestore,
      });

      const loraChanged = nextParams.loraModels !== generator.params.loraModels;
      const cnChanged = nextParams.controlnets !== generator.params.controlnets;
      const addOnsChanged = nextParams.addOns !== generator.params.addOns;
      const hiresChanged = nextParams.hiresOptions !== generator.params.hiresOptions;

      if (nextParams.checkpoint) {
        nextBaseType = nextParams.baseType = baseTypeToNumber(nextParams.checkpoint.baseType) || MODEL_SDXL;
      }

      if (cnChanged || loraChanged || addOnsChanged || hiresChanged) {
        nextParams = fixControlNets(nextParams, generator.params, generator.type);
      }

      if (nextBaseType && prevBaseType !== nextBaseType) {
        nextParams = fixGenerateParamsForBaseType(nextParams, generator.params);
      }

      if (cnChanged) {
        nextParams = fixGenerateParamsForControlNets(nextParams, generator.params);
      }

      return { params: nextParams };
    },
    options,
  );
};

export const updateTask = (generatorId, data) => {
  const { taskMap, updateCanvasData } = getCanvasState();
  const prevTaskData = taskMap[generatorId] || {};

  updateCanvasData({
    taskMap: Object.assign({}, taskMap, {
      [generatorId]: Object.assign({}, prevTaskData, executeOrReturn(data, prevTaskData)),
    }),
  });
};

export const clearGenerateTask = (generatorId) => {
  const { taskMap, updateCanvasData } = getCanvasState();

  updateCanvasData({
    taskMap: Object.assign({}, taskMap, {
      [generatorId]: { ...initialTaskData },
    }),
  });
};

export const updateReference = (generatorId, reference, options) => {
  updateGenerateParams(
    generatorId,
    (params) => ({ reference: Object.assign({}, params.reference, executeOrReturn(reference, params.reference)) }),
    options,
  );
};

export const updateControlNet = (generatorId, id, controlNetData, options) => {
  updateGenerateParams(
    generatorId,
    (params) => ({
      controlnets: params.controlnets.map((item) => {
        return item.id !== id ? item : Object.assign({}, item, executeOrReturn(controlNetData, item));
      }),
    }),
    options,
  );
};

export const appendControlNet = (generatorId, controlNetData, options) => {
  updateGenerateParams(
    generatorId,
    (params) => ({
      controlnets: params.controlnets.concat(controlNetData),
    }),
    options,
  );
};

window.SHAKKER = {
  getCanvasState,
  setCanvasState,
};
